import { Flex, Icon, Image, Input, Text, InputRightElement, IconButton } from '@chakra-ui/react';
import { FunctionComponent, forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { BiImageAdd } from 'react-icons/bi';
import FeatherIcon from '@ailibs/feather-react-ts';

type LogoUploadProps = {
  imageURL: string;
  onFileChange: (file: File | undefined) => void;
  error?: string;
};

export const LogoUpload: FunctionComponent<LogoUploadProps> = forwardRef<HTMLInputElement, LogoUploadProps>((props, ref) => {
  const { onFileChange, error, imageURL } = props;

  const [file, setFile] = useState<File | undefined>(undefined);

  const innerRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  const onHandleFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event?.target?.files?.[0];
      setFile(file);
      onFileChange(file);
    },
    [onFileChange],
  );

  const imageURLName = imageURL ? imageURL.split('/').pop() : '';
  const imageName = file ? file.name : imageURLName;

  return (
    <Flex width='100%' gap={6}>
      {!file && !imageURL && (
        <Image
          src={undefined}
          border={'1px solid black'}
          borderRadius='8px'
          minW='100px'
          maxW='100px'
          minH='100px'
          maxH='100px'
          objectFit='cover'
          overflow='hidden'
        />
      )}

      {!file && imageURL && (
        <Image
          src={imageURL || undefined}
          border={imageURL ? '' : '1px solid black'}
          borderRadius='8px'
          minW='100px'
          maxW='100px'
          minH='100px'
          maxH='100px'
          objectFit='cover'
          overflow='hidden'
        />
      )}

      {file && (
        <Image
          src={URL.createObjectURL(file)}
          border={file ? '' : '1px solid black'}
          borderRadius='8px'
          minW='100px'
          maxW='100px'
          minH='100px'
          maxH='100px'
          objectFit='cover'
          overflow='hidden'
        />
      )}

      <Flex alignItems='center' gap={6} width='100%'>
        <Flex position='relative' width='100%' direction='column' gap={2}>
          <Input type='file' display='none' ref={innerRef} accept='.png, .jpg, .jpeg' onChange={onHandleFile} />
          <Input
            variant='flushed'
            width='100%'
            value={imageName}
            readOnly
            cursor='pointer'
            borderColor={error ? 'red.500' : undefined}
            borderBottomWidth={error ? '2px' : undefined}
            onClick={() => innerRef?.current?.click()}
            fontSize={12}
          />
          <InputRightElement cursor='pointer' onClick={() => innerRef?.current?.click()}>
            <Icon as={BiImageAdd} size={24} color={error ? 'red.500' : 'brand.500'} />
          </InputRightElement>
          <Text fontSize='12px' color='grey.500'>
            Formato: PNG ou JPG | Dimensões 1024 x 1024 px
          </Text>

          {error ? (
            <Text fontSize='14px' color='red.500'>
              {error}
            </Text>
          ) : null}
        </Flex>

        <IconButton
          aria-label='Deletar imagem'
          icon={<FeatherIcon name='trash-2' />}
          variant='unstyled'
          size='18px'
          color='red.500'
          cursor='pointer'
          onClick={() => {
            if (innerRef?.current) innerRef.current.files = null;
            setFile(undefined);
            onFileChange(undefined);
          }}
        />
      </Flex>
    </Flex>
  );
});
